import React, { useState } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Avatar,
  Stack
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  List as ListIcon,
  Description as DocumentIcon,
  Payments as PaymentsIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Home as HomeIcon,
  ExpandLess,
  ExpandMore,
  ChevronLeft as ChevronLeftIcon
} from '@mui/icons-material';
import { ReactComponent as DatabaseIcon } from './assets/icon/database_24dp_E3E3E3_FILL0_wght400_GRAD0_opsz24.svg';

// Pages
import Dashboard from './pages/Dashboard';
import Groups from './pages/masterfiles/Groups';
import Stores from './pages/masterfiles/Stores';
import Personnel from './pages/masterfiles/Personnel';
import GeneratePayroll from './pages/payroll/GeneratePayroll';
import ManagePayroll from './pages/payroll/ManagePayroll';
import SavePayrolls from './pages/payroll/SavePayrolls';
import Payslips from './pages/payroll/Payslips';
import GovernmentBenefits from './pages/reports/fgi/GovernmentBenefits';

// App no longer renders public auth routes. Only private app UI below.
import { useAuth } from './context/AuthContext';
import StatusBadge from './components/StatusBadge';
import SetPayrollAuthoritiesDialog from './components/SetPayrollAuthoritiesDialog';
import { getAccessibleDeductionDepartments } from './pages/payroll/Deductions/access';

const drawerWidth = 240;

function App() {
  const [open, setOpen] = useState(true);
  const [masterfilesOpen, setMasterfilesOpen] = useState(false);
  const [payrollAuthoritiesDialogOpen, setPayrollAuthoritiesDialogOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [payrollOpen, setPayrollOpen] = useState(false);
  const [deductionsOpen, setDeductionsOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isSignInPage = false; // App hosts only private routes now

  const userPosition = user?.position || '';
  const isFGIUser = userPosition.toLowerCase().includes('fgi');
  const payrollAccessControl = user?.payrollAccessControl || {};
  const canSeeMasterfile = payrollAccessControl.masterfile !== false;
  const canSeePayroll = payrollAccessControl.payroll !== false;
  const canSeeEmployees = payrollAccessControl.employees !== false;
  const canSeeReports = payrollAccessControl.reports !== false;
  const canSeeSettings = payrollAccessControl.settings !== false;

  // For demo purposes - in a real app, you'd check for authentication tokens
  // Uncomment this to bypass login screen during development
  // useState(() => { setIsAuthenticated(true); }, []);

  const handleSettingsClick = () => {
    setSettingsOpen(!settingsOpen);
  }
  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleMasterfilesClick = () => {
    setMasterfilesOpen(!masterfilesOpen);
  };

  const handlePayrollClick = () => {
    setPayrollOpen(!payrollOpen);
  };

  const handleDeductionsClick = () => {
    setDeductionsOpen(!deductionsOpen);
  };

  const handleReportsClick = () => {
    setReportsOpen(!reportsOpen);
  };

  const handleLogout = () => {
    logout();
  };

  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };

  const accessibleDeductions = getAccessibleDeductionDepartments(user);
  const deductionsActive = isActive('/deductions');
  const singleDeductionAccess = accessibleDeductions.length === 1 ? accessibleDeductions[0] : null;



  return (
    <Box>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${open ? drawerWidth : 0}px)` },
          ml: { sm: `${open ? drawerWidth : 0}px` },
          bgcolor: 'white',
          color: 'text.primary',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          display: { xs: 'block', md: 'none' } // Only show on mobile
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>APEX Payroll System</Typography>
          <StatusBadge />
        </Toolbar>
      </AppBar>
      <Drawer
        variant="persistent"
        open={open}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            background: 'linear-gradient(135deg, #0FABDB 0%, #10C4DB 100%)',
            color: 'white'
          },
        }}
      >
        {/* APEX Logo and Title */}
        <Box sx={{ p: 2, textAlign: 'left', mt: 1 }}>
          <Box 
            sx={{ 
              bgcolor: 'white', 
              borderRadius: 4, 
              py: 1, 
              px: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: 'fit-content',
              mx: 'auto'
            }}
          >
            <Typography 
              variant="h4" 
              component="div" 
              sx={{ 
                fontWeight: 'bold', 
                color: '#12C1DC',
                letterSpacing: 1,
                fontSize: '4rem'
              }}
            >
              APEX
            </Typography>
            <Typography 
              variant="subtitle2" 
              component="div" 
              sx={{ 
                color: '#12C1DC',
                letterSpacing: 2,
                textTransform: 'uppercase',
                fontSize: '1rem',
                mt: -0.5
              }}
            >
              PAYROLL SYSTEM
            </Typography>
          </Box>
        </Box>
        
        {/* User Profile */}
        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          alignItems: 'center',
          mb: 1
        }}>
          <Avatar 
            sx={{ 
              width: 50, 
              height: 50,
              bgcolor: 'white',
              color: '#12C1DC',
              mr: 2
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{(user?.firstName?.[0] || 'U')}{(user?.lastName?.[0] || '')}</Typography>
          </Avatar>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1, minWidth: 0, }}>
            <Typography variant="body1" sx={{ fontWeight: 'medium', color: 'white', lineHeight: 1.2, wordBreak: 'break-word' }}>
              {user?.firstName} {user?.lastName}
            </Typography>
            {user?.position && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', wordBreak: 'break-word' }}>
                {user.position}
              </Typography>
            )}
          </Box>
        </Box>
        
        <Divider sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
        
        {/* Navigation Menu */}
        <List sx={{ p: 0 }}>
                    {/* Dashboard */}
{/*          <ListItem disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              component={Link}
              to="/"
              selected={location.pathname === '/'}
              sx={{
                py: 1.5,
                position: 'relative',
                margin: '10px',
                borderRadius: '10px',
                '&.Mui-selected': {
                  bgcolor: '#0091FF',
                  '&:hover': {
                    bgcolor: '#0091FF',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: '6px',
                    top: '8px',
                    bottom: '8px',
                    width: '6px',
                    backgroundColor: 'white',
                    borderRadius: '4px',
                  },
                },
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                  <HomeIcon />
                </ListItemIcon>
                <ListItemText primary="Dashboard" />
              </Box>
            </ListItemButton>
          </ListItem>*/}
          {/* Payroll */}
          {!isFGIUser && canSeePayroll && (
          <Box sx={{ position: 'relative', margin: '10px' }}>
            {(payrollOpen && (isActive('/payroll/generate') || isActive('/payroll/manage') || isActive('/payroll/save') || isActive('/payroll/payslips') || isActive('/payroll/view'))) && (
              <Box
                sx={{
                  position: 'absolute',
                  left: '6px',
                  top: '8px',
                  bottom: '8px',
                  width: '6px',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  zIndex: 2,
                }}
              />
            )}
            <ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton 
                onClick={handlePayrollClick}
                selected={payrollOpen || isActive('/payroll/generate') || isActive('/payroll/manage') || isActive('/payroll/save') || isActive('/payroll/payslips') || isActive('/payroll/view')}
                sx={{
                  py: 1.5,
                  position: 'relative',
                  borderRadius: '10px',
                  bgcolor: isActive('/payroll/generate') || isActive('/payroll/manage') || isActive('/payroll/save') || isActive('/payroll/payslips') || isActive('/payroll/view') ? '#0091FF' : 'transparent',
                  '&.Mui-selected': {
                    bgcolor: isActive('/payroll/generate') || isActive('/payroll/manage') || isActive('/payroll/save') || isActive('/payroll/payslips') || isActive('/payroll/view') ? '#0091FF' : 'transparent',
                    '&:hover': {
                      bgcolor: isActive('/payroll/generate') || isActive('/payroll/manage') || isActive('/payroll/save') || isActive('/payroll/payslips') || isActive('/payroll/view') ? '#0091FF' : 'rgba(255, 255, 255, 0.1)',
                    },
                    '&::before': (isActive('/payroll/generate') || isActive('/payroll/manage') || isActive('/payroll/save') || isActive('/payroll/payslips') || isActive('/payroll/view')) && !payrollOpen ? {
                      content: '""',
                      position: 'absolute',
                      left: '6px',
                      top: '8px',
                      bottom: '8px',
                      width: '6px',
                      backgroundColor: 'white',
                      borderRadius: '4px',
                    } : {},
                  },
                  '&:hover': {
                    bgcolor: isActive('/payroll/generate') || isActive('/payroll/manage') || isActive('/payroll/save') || isActive('/payroll/payslips') || isActive('/payroll/view') ? '#0091FF' : 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                      <DatabaseIcon />
                    </ListItemIcon>
                    <ListItemText primary="Payroll" />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {payrollOpen ? <ExpandLess sx={{ color: 'white' }} /> : <ExpandMore sx={{ color: 'white' }} />}
                  </Box>
                </Box>
              </ListItemButton>
            </ListItem>
            <Collapse in={payrollOpen} timeout="auto" unmountOnExit>
              <Box sx={{ bgcolor: isActive('/payroll/generate') || isActive('/payroll/manage') || isActive('/payroll/save') || isActive('/payroll/payslips') || isActive('/payroll/view') ? '#0091FF' : 'transparent', borderRadius: '0 0 10px 10px', mt: -1 }}>
                <List component="div" disablePadding>
                  <ListItemButton
                    component={Link}
                    to="/payroll/manage"
                    selected={isActive('/payroll/manage')}
                    sx={{
                      pl: 4,
                      py: 1,
                      position: 'relative',
                      '&.Mui-selected': {
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.15)',
                        },
                      },
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box 
                        sx={{ 
                          width: isActive('/payroll/manage') ? '10px' : '6px', 
                          height: isActive('/payroll/manage') ? '10px' : '6px', 
                          borderRadius: '50%', 
                          bgcolor: 'white', 
                          mr: 1.5,
                          transition: 'all 0.2s ease-in-out'
                        }} 
                      />
                      <ListItemText 
                        primary="Manage Payroll" 
                        primaryTypographyProps={{
                          fontWeight: isActive('/payroll/manage') ? 'bold' : 'normal'
                        }}
                      />
                    </Box>
                  </ListItemButton>
                  <ListItemButton
                    component={Link}
                    to="/payroll/save"
                    selected={isActive('/payroll/save')}
                    sx={{
                      pl: 4,
                      py: 1,
                      position: 'relative',
                      '&.Mui-selected': {
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.15)',
                        },
                      },
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box 
                        sx={{ 
                          width: isActive('/payroll/save') ? '10px' : '6px', 
                          height: isActive('/payroll/save') ? '10px' : '6px', 
                          borderRadius: '50%', 
                          bgcolor: 'white', 
                          mr: 1.5,
                          transition: 'all 0.2s ease-in-out'
                        }} 
                      />
                      <ListItemText 
                        primary="Saved Payrolls" 
                        primaryTypographyProps={{
                          fontWeight: isActive('/payroll/save') ? 'bold' : 'normal'
                        }}
                      />
                    </Box>
                  </ListItemButton>
                  <ListItemButton
                    component={Link}
                    to="/payroll/payslips"
                    selected={isActive('/payroll/payslips')}
                    sx={{
                      pl: 4,
                      py: 1,
                      position: 'relative',
                      '&.Mui-selected': {
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.15)',
                        },
                      },
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box 
                        sx={{ 
                          width: isActive('/payroll/payslips') ? '10px' : '6px', 
                          height: isActive('/payroll/payslips') ? '10px' : '6px', 
                          borderRadius: '50%', 
                          bgcolor: 'white', 
                          mr: 1.5,
                          transition: 'all 0.2s ease-in-out'
                        }} 
                      />
                      <ListItemText 
                        primary="Payslips" 
                        primaryTypographyProps={{
                          fontWeight: isActive('/payroll/payslips') ? 'bold' : 'normal'
                        }}
                      />
                    </Box>
                  </ListItemButton>
                  <ListItemButton
                    component={Link}
                    to="/payroll/13thmonth"
                    selected={isActive('/payroll/13thmonth')}
                    sx={{
                      pl: 4,
                      py: 1,
                      position: 'relative',
                      '&.Mui-selected': {
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.15)',
                        },
                      },
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box 
                        sx={{ 
                          width: isActive('/payroll/13thmonth') ? '10px' : '6px', 
                          height: isActive('/payroll/13thmonth') ? '10px' : '6px', 
                          borderRadius: '50%', 
                          bgcolor: 'white', 
                          mr: 1.5,
                          transition: 'all 0.2s ease-in-out'
                        }} 
                      />
                      <ListItemText 
                        primary="13th Month Pay" 
                        primaryTypographyProps={{
                          fontWeight: isActive('/payroll/13thmonth') ? 'bold' : 'normal'
                        }}
                      />
                    </Box>
                  </ListItemButton>
                </List>
              </Box>
            </Collapse>
          </Box>
          )}
          {/* Deductions */}
          {user?.role !== 'store' && accessibleDeductions.length > 0 && (
          <Box sx={{ position: 'relative', margin: '10px' }}>
            {(deductionsOpen && deductionsActive) && (
              <Box
                sx={{
                  position: 'absolute',
                  left: '6px',
                  top: '8px',
                  bottom: '8px',
                  width: '6px',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  zIndex: 2,
                }}
              />
            )}
            <ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                onClick={() => {
                  if (singleDeductionAccess) {
                    navigate(singleDeductionAccess.path);
                    return;
                  }

                  handleDeductionsClick();
                }}
                selected={deductionsOpen || deductionsActive}
                sx={{
                  py: 1.5,
                  position: 'relative',
                  borderRadius: '10px',
                  bgcolor: deductionsActive ? '#0091FF' : 'transparent',
                  '&.Mui-selected': {
                    bgcolor: deductionsActive ? '#0091FF' : 'transparent',
                    '&:hover': {
                      bgcolor: deductionsActive ? '#0091FF' : 'rgba(255, 255, 255, 0.1)',
                    },
                    '&::before': deductionsActive && !deductionsOpen ? {
                      content: '""',
                      position: 'absolute',
                      left: '6px',
                      top: '8px',
                      bottom: '8px',
                      width: '6px',
                      backgroundColor: 'white',
                      borderRadius: '4px',
                    } : {},
                  },
                  '&:hover': {
                    bgcolor: deductionsActive ? '#0091FF' : 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                      <PaymentsIcon />
                    </ListItemIcon>
                    <ListItemText primary="Deductions" />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {!singleDeductionAccess && (
                      deductionsOpen ? <ExpandLess sx={{ color: 'white' }} /> : <ExpandMore sx={{ color: 'white' }} />
                    )}
                  </Box>
                </Box>
              </ListItemButton>
            </ListItem>
            <Collapse in={!singleDeductionAccess && (deductionsOpen || deductionsActive)} timeout="auto" unmountOnExit>
              <Box sx={{ bgcolor: deductionsActive ? '#0091FF' : 'transparent', borderRadius: '0 0 10px 10px', mt: -1 }}>
                <List component="div" disablePadding>
                  {accessibleDeductions.map((item) => (
                    <ListItemButton
                      key={item.path}
                      component={Link}
                      to={item.path}
                      selected={isActive(item.path)}
                      sx={{
                        pl: 4,
                        py: 1,
                        position: 'relative',
                        '&.Mui-selected': {
                          bgcolor: 'rgba(255, 255, 255, 0.1)',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.15)',
                          },
                        },
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.05)',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                          sx={{
                            width: isActive(item.path) ? '10px' : '6px',
                            height: isActive(item.path) ? '10px' : '6px',
                            borderRadius: '50%',
                            bgcolor: 'white',
                            mr: 1.5,
                            transition: 'all 0.2s ease-in-out'
                          }}
                        />
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontWeight: isActive(item.path) ? 'bold' : 'normal'
                          }}
                        />
                      </Box>
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            </Collapse>
          </Box>
          )}
          {/* Masterfiles */}
          {!isFGIUser && canSeeMasterfile && (
          <Box sx={{ position: 'relative', margin: '10px' }}>
            {(masterfilesOpen && (isActive('/masterfiles/groups') || isActive('/masterfiles/stores') || isActive('/masterfiles/personnel'))) && (
              <Box
                sx={{
                  position: 'absolute',
                  left: '6px',
                  top: '8px',
                  bottom: '8px',
                  width: '6px',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  zIndex: 2,
                }}
              />
            )}
            <ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton 
                onClick={handleMasterfilesClick}
                selected={masterfilesOpen || isActive('/masterfiles/groups') || isActive('/masterfiles/stores') || isActive('/masterfiles/personnel')}
                sx={{
                  py: 1.5,
                  position: 'relative',
                  borderRadius: '10px',
                  bgcolor: isActive('/masterfiles/groups') || isActive('/masterfiles/stores') || isActive('/masterfiles/personnel') ? '#0091FF' : 'transparent',
                  '&.Mui-selected': {
                    bgcolor: isActive('/masterfiles/groups') || isActive('/masterfiles/stores') || isActive('/masterfiles/personnel') ? '#0091FF' : 'transparent',
                    '&:hover': {
                      bgcolor: isActive('/masterfiles/groups') || isActive('/masterfiles/stores') || isActive('/masterfiles/personnel') ? '#0091FF' : 'rgba(255, 255, 255, 0.1)',
                    },
                    '&::before': (isActive('/masterfiles/groups') || isActive('/masterfiles/stores') || isActive('/masterfiles/personnel')) && !masterfilesOpen ? {
                      content: '""',
                      position: 'absolute',
                      left: '6px',
                      top: '8px',
                      bottom: '8px',
                      width: '6px',
                      backgroundColor: 'white',
                      borderRadius: '4px',
                    } : {},
                  },
                  '&:hover': {
                    bgcolor: isActive('/masterfiles/groups') || isActive('/masterfiles/stores') || isActive('/masterfiles/personnel') ? '#0091FF' : 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                      <ListIcon />
                    </ListItemIcon>
                    <ListItemText primary="Masterfile" />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {masterfilesOpen ? <ExpandLess sx={{ color: 'white' }} /> : <ExpandMore sx={{ color: 'white' }} />}
                  </Box>
                </Box>
              </ListItemButton>
            </ListItem>
            <Collapse in={masterfilesOpen} timeout="auto" unmountOnExit>
              <Box sx={{ bgcolor: isActive('/masterfiles/groups') || isActive('/masterfiles/stores') || isActive('/masterfiles/personnel') ? '#0091FF' : 'transparent', borderRadius: '0 0 10px 10px', mt: -1 }}>
                <List component="div" disablePadding>
                  <ListItemButton
                    component={Link}
                    to="/masterfiles/groups"
                    selected={isActive('/masterfiles/groups')}
                    sx={{
                      pl: 4,
                      py: 1,
                      position: 'relative',
                      '&.Mui-selected': {
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.15)',
                        },
                      },
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box 
                        sx={{ 
                          width: isActive('/masterfiles/groups') ? '10px' : '6px', 
                          height: isActive('/masterfiles/groups') ? '10px' : '6px', 
                          borderRadius: '50%', 
                          bgcolor: 'white', 
                          mr: 1.5,
                          transition: 'all 0.2s ease-in-out'
                        }} 
                      />
                      <ListItemText 
                        primary="Groups" 
                        primaryTypographyProps={{
                          fontWeight: isActive('/masterfiles/groups') ? 'bold' : 'normal'
                        }}
                      />
                    </Box>
                  </ListItemButton>
                  <ListItemButton
                    component={Link}
                    to="/masterfiles/stores"
                    selected={isActive('/masterfiles/stores')}
                    sx={{
                      pl: 4,
                      py: 1,
                      position: 'relative',
                      '&.Mui-selected': {
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.15)',
                        },
                      },
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box 
                        sx={{ 
                          width: isActive('/masterfiles/stores') ? '10px' : '6px', 
                          height: isActive('/masterfiles/stores') ? '10px' : '6px', 
                          borderRadius: '50%', 
                          bgcolor: 'white', 
                          mr: 1.5,
                          transition: 'all 0.2s ease-in-out'
                        }} 
                      />
                      <ListItemText 
                        primary="Stores" 
                        primaryTypographyProps={{
                          fontWeight: isActive('/masterfiles/stores') ? 'bold' : 'normal'
                        }}
                      />
                    </Box>
                  </ListItemButton>
                  <ListItemButton
                    component={Link}
                    to="/masterfiles/personnel"
                    selected={isActive('/masterfiles/personnel')}
                    sx={{
                      pl: 4,
                      py: 1,
                      position: 'relative',
                      '&.Mui-selected': {
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.15)',
                        },
                      },
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box 
                        sx={{ 
                          width: isActive('/masterfiles/personnel') ? '10px' : '6px', 
                          height: isActive('/masterfiles/personnel') ? '10px' : '6px', 
                          borderRadius: '50%', 
                          bgcolor: 'white', 
                          mr: 1.5,
                          transition: 'all 0.2s ease-in-out'
                        }} 
                      />
                      <ListItemText 
                        primary="Personnel" 
                        primaryTypographyProps={{
                          fontWeight: isActive('/masterfiles/personnel') ? 'bold' : 'normal'
                        }}
                      />
                    </Box>
                  </ListItemButton>
                </List>
              </Box>
            </Collapse>
          </Box>
          )}

         

          {/* Employees */}
          {user?.role !== 'store' && !isFGIUser && canSeeEmployees && (
          <ListItem disablePadding sx={{ display: 'block', margin: '10px' }}>
            <ListItemButton
              component={Link}
              to="/employees"
              selected={isActive('/employees')}
              sx={{
                py: 1.5,
                borderRadius: '10px',
                '&.Mui-selected': {
                  bgcolor: '#0091FF',
                  '&:hover': { bgcolor: '#0091FF' },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: '6px',
                    top: '8px',
                    bottom: '8px',
                    width: '6px',
                    backgroundColor: 'white',
                    borderRadius: '4px',
                  },
                },
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                  <ListIcon />
                </ListItemIcon>
                <ListItemText primary="Employees" />
              </Box>
            </ListItemButton>
          </ListItem>
          )}
          {/* Reports */}
          {canSeeReports && (
          <Box sx={{ position: 'relative', margin: '10px' }}>
            {/* Extended white indicator for expanded category */}
            {(reportsOpen && isActive('/reports/government-benefits')) && (
              <Box
                sx={{
                  position: 'absolute',
                  left: '6px',
                  top: '8px',
                  bottom: '8px',
                  width: '6px',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  zIndex: 2,
                }}
              />
            )}
            <ListItem disablePadding sx={{ display: 'block' }}>
            <ListItemButton 
              onClick={handleReportsClick}
              selected={reportsOpen || isActive('/reports/government-benefits')}
              sx={{
                py: 1.5,
                position: 'relative',
                borderRadius: '10px',
                bgcolor: isActive('/reports/government-benefits') ? '#0091FF' : 'transparent',
                '&.Mui-selected': {
                  bgcolor: isActive('/reports/government-benefits') ? '#0091FF' : 'transparent',
                  '&:hover': {
                    bgcolor: isActive('/reports/government-benefits') ? '#0091FF' : 'rgba(255, 255, 255, 0.1)',
                  },
                  '&::before': isActive('/reports/government-benefits') && !reportsOpen ? {
                    content: '""',
                    position: 'absolute',
                    left: '6px',
                    top: '8px',
                    bottom: '8px',
                    width: '6px',
                    backgroundColor: 'white',
                    borderRadius: '4px',
                  } : {},
                },
                '&:hover': {
                  bgcolor: isActive('/reports/government-benefits') ? '#0091FF' : 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                    <DocumentIcon />
                  </ListItemIcon>
                  <ListItemText primary="Reports" />
                </Box>
                {/* Right-aligned arrow icon */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {reportsOpen ? <ExpandLess sx={{ color: 'white' }} /> : <ExpandMore sx={{ color: 'white' }} />}
                </Box>
              </Box>
            </ListItemButton>
          </ListItem>
          <Collapse in={reportsOpen} timeout="auto" unmountOnExit>
            <Box sx={{ bgcolor: isActive('/reports/government-benefits') ? '#0091FF' : 'transparent', borderRadius: '0 0 10px 10px', mt: -1 }}>
            <List component="div" disablePadding>
              <ListItemButton
                component={Link}
                to="/reports/government-benefits"
                selected={isActive('/reports/government-benefits')}
                sx={{
                  pl: 4,
                  py: 1,
                  position: 'relative',
                  '&.Mui-selected': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.15)',
                    },
                  },
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box 
                    sx={{ 
                      width: isActive('/reports/government-benefits') ? '10px' : '6px', 
                      height: isActive('/reports/government-benefits') ? '10px' : '6px', 
                      borderRadius: '50%', 
                      bgcolor: 'white', 
                      mr: 1.5,
                      transition: 'all 0.2s ease-in-out'
                    }} 
                  />
                  <ListItemText 
                    primary="Government Benefits" 
                    primaryTypographyProps={{
                      fontWeight: isActive('/reports/government-benefits') ? 'bold' : 'normal'
                    }}
                  />
                </Box>
              </ListItemButton>
            </List>
            </Box>
          </Collapse>
          </Box>
          )}

          {/* Settings */}
          {canSeeSettings && (
          <Box sx={{ position: 'relative', margin: '10px' }}>
            <ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton 
                onClick={handleSettingsClick}
                selected={settingsOpen}
                sx={{
                  py: 1.5,
                  position: 'relative',
                  borderRadius: '10px',
                  bgcolor: settingsOpen ? '#0091FF' : 'transparent',
                  '&.Mui-selected': {
                    bgcolor: settingsOpen ? '#0091FF' : 'transparent',
                    '&:hover': {
                      bgcolor: settingsOpen ? '#0091FF' : 'rgba(255, 255, 255, 0.1)',
                    },
                  },
                  '&:hover': {
                    bgcolor: settingsOpen ? '#0091FF' : 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                      <SettingsIcon />
                    </ListItemIcon>
                    <ListItemText primary="Settings" />
                  </Box>
                  {/* Right-aligned arrow icon */}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {settingsOpen ? <ExpandLess sx={{ color: 'white' }} /> : <ExpandMore sx={{ color: 'white' }} />}
                  </Box>
                </Box>
              </ListItemButton>
            </ListItem>
            <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
              <Box sx={{ bgcolor: settingsOpen ? '#0091FF' : 'transparent', borderRadius: '0 0 10px 10px', mt: -1 }}>
                <List component="div" disablePadding>
                  <ListItemButton
                    onClick={() => setPayrollAuthoritiesDialogOpen(true)}
                    sx={{
                      pl: 4,
                      py: 1,
                      position: 'relative',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box 
                        sx={{ 
                          width: '6px', 
                          height: '6px', 
                          borderRadius: '50%', 
                          bgcolor: 'white', 
                          mr: 1.5,
                        }} 
                      />
                      <ListItemText 
                        primary="Set Payroll Authorities" 
                      />
                    </Box>
                  </ListItemButton>
                </List>
              </Box>
            </Collapse>
          </Box>
          )}
        </List>
        
        {/* Logout Button at Bottom */}
        <Box sx={{ 
          mt: 'auto', 
          p: 2, 
          borderTop: '1px solid rgba(255,255,255,0.2)',
          display: 'flex',
          justifyContent: 'center'
        }}>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              py: 1.5,
              px: 3,
              borderRadius: 2,
              bgcolor: '#0091FF',
              '&:hover': {
                bgcolor: '#0FABDB',
              },
              width: 'auto',
              justifyContent: 'center'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </Box>
          </ListItemButton>
        </Box>
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${open ? drawerWidth : 0}px)` },
          ml: { sm: `${open ? drawerWidth : 0}px` },
          transition: theme => theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Outlet />
      </Box>
      <SetPayrollAuthoritiesDialog
        open={payrollAuthoritiesDialogOpen}
        onClose={() => setPayrollAuthoritiesDialogOpen(false)}
      />
    </Box>
  );
}

export default App;

